import type { BlisterSettings } from "../types/settings";
import { randomizeBlisterSettings } from "../lib/blister-settings";
import { Dices } from "lucide-react";
import SpotifyIcon from "../assets/spotify.svg?react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import { Switch } from "./ui/switch";

interface BlisterSettingsPanelProps {
  settings: BlisterSettings;
  onChange: (settings: BlisterSettings) => void;
  canApplySpotifyColors?: boolean;
  onApplySpotifyColors?: () => void;
}

type BlisterSettingKey = keyof BlisterSettings;

const BLISTER_SECTION_KEYS = {
  palette: ["colors"],
  mainParticles: [
    "particleSize",
    "speed",
    "count",
    "opacity",
    "additive",
    "speedRandomness",
    "sizeRandomness",
    "glowStrength",
    "blurStrength",
    "glowFraction",
    "blurFraction",
    "windStrength",
    "windFraction",
  ],
  atmosphere: [
    "atmosphereIntensity",
    "ambientDriftSpeed",
    "hazeAmount",
    "fieldDistortion",
    "depthSoftness",
  ],
  sparkles: [
    "sparkleCount",
    "sparkleSize",
    "sparkleOpacity",
    "sparkleReactivity",
    "sparkleRandomness",
  ],
  reactiveMotion: [
    "pulseIntensity",
    "pulseFrequency",
    "trailAmount",
    "afterimageAmount",
    "flashSoftness",
  ],
  postProcessing: [
    "bloomIntensity",
    "bloomRadius",
    "grainAmount",
    "vignetteAmount",
    "chromaticAberration",
  ],
} satisfies Record<string, BlisterSettingKey[]>;

function pickBlisterSettings(
  source: BlisterSettings,
  keys: readonly BlisterSettingKey[]
): Partial<BlisterSettings> {
  return keys.reduce<Partial<BlisterSettings>>((accumulator, key) => {
    accumulator[key] = source[key];
    return accumulator;
  }, {});
}

function formatValue(value: number) {
  return Number.isInteger(value) ? value.toString() : value.toFixed(2).replace(/\.00$/, "").replace(/0$/, "");
}

function SettingsSection({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-white/10 bg-white/5 p-3">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-white">{title}</h4>
          {description ? <p className="mt-1 text-xs text-white/60">{description}</p> : null}
        </div>
        {action}
      </div>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
}

function SectionRandomButton({ onClick, title = "Randomize section" }: { onClick: () => void; title?: string }) {
  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className="size-8 cursor-pointer border-white/20 bg-transparent text-white hover:bg-white/10"
      onClick={onClick}
      title={title}
    >
      <Dices className="size-4" />
    </Button>
  );
}

function SliderField({
  id,
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
}) {
  return (
    <div>
      <Label htmlFor={id} className="block mb-2">
        {label} <span className="text-gray-400">({formatValue(value)})</span>
      </Label>
      <Slider
        id={id}
        min={min}
        max={max}
        step={step}
        value={[value]}
        onValueChange={([next]) => onChange(next)}
        className="w-full"
      />
    </div>
  );
}

function ToggleField({
  id,
  label,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <Label htmlFor={id} className="block">
        {label}
      </Label>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

export function BlisterSettingsPanel({
  settings,
  onChange,
  canApplySpotifyColors = false,
  onApplySpotifyColors,
}: BlisterSettingsPanelProps) {
  const randomizeSection = (keys: readonly BlisterSettingKey[]) => {
    const randomized = randomizeBlisterSettings();
    onChange({
      ...settings,
      ...pickBlisterSettings(randomized, keys),
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold">✨ Blister Settings</h3>

      <SettingsSection
        title="Palette"
        description="Keep the scene rooted in a restrained color atmosphere."
        action={<SectionRandomButton onClick={() => randomizeSection(BLISTER_SECTION_KEYS.palette)} />}
      >
        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <Label className="block">
              <b>Colors:</b>
            </Label>
            {canApplySpotifyColors && onApplySpotifyColors ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="cursor-pointer gap-2 border-white/20 bg-transparent text-white hover:bg-white/10"
                title="Apply colors from Spotify"
                onClick={onApplySpotifyColors}
              >
                <SpotifyIcon className="size-4" />
                Spotify Colors
              </Button>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            {settings.colors.map((c: string, i: number) => (
              <Input
                key={i}
                type="color"
                value={c}
                className="cursor-pointer w-20 h-10 rounded border-none"
                onChange={(e) => {
                  const arr = [...settings.colors];
                  arr[i] = e.target.value;
                  onChange({ ...settings, colors: arr });
                }}
              />
            ))}
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        title="Main Particles"
        description="Hero layer density, glow, softness, and flow."
        action={<SectionRandomButton onClick={() => randomizeSection(BLISTER_SECTION_KEYS.mainParticles)} />}
      >
        <SliderField
          id="particleSize"
          label="Particle Size"
          value={settings.particleSize}
          min={0.1}
          max={1.8}
          step={0.05}
          onChange={(value) => onChange({ ...settings, particleSize: value })}
        />
        <SliderField
          id="speed"
          label="Speed"
          value={settings.speed}
          min={0.01}
          max={0.24}
          step={0.005}
          onChange={(value) => onChange({ ...settings, speed: value })}
        />
        <SliderField
          id="count"
          label="Particle Count"
          value={settings.count}
          min={100}
          max={5000}
          step={100}
          onChange={(value) => onChange({ ...settings, count: value })}
        />
        <SliderField
          id="opacity"
          label="Opacity"
          value={settings.opacity}
          min={0.2}
          max={1}
          step={0.05}
          onChange={(value) => onChange({ ...settings, opacity: value })}
        />
        <ToggleField
          id="additive"
          label="Additive Blending"
          checked={settings.additive}
          onChange={(checked) => onChange({ ...settings, additive: checked })}
        />
        <SliderField
          id="speedRandomness"
          label="Speed Randomness"
          value={settings.speedRandomness}
          min={0}
          max={2.4}
          step={0.1}
          onChange={(value) => onChange({ ...settings, speedRandomness: value })}
        />
        <SliderField
          id="sizeRandomness"
          label="Size Randomness"
          value={settings.sizeRandomness}
          min={0}
          max={3}
          step={0.1}
          onChange={(value) => onChange({ ...settings, sizeRandomness: value })}
        />
        <SliderField
          id="glowStrength"
          label="Glow Strength"
          value={settings.glowStrength}
          min={0}
          max={20}
          step={0.5}
          onChange={(value) => onChange({ ...settings, glowStrength: value })}
        />
        <SliderField
          id="blurStrength"
          label="Blur Strength"
          value={settings.blurStrength}
          min={0}
          max={20}
          step={0.5}
          onChange={(value) => onChange({ ...settings, blurStrength: value })}
        />
        <SliderField
          id="glowFraction"
          label="Glow Fraction"
          value={settings.glowFraction}
          min={0.02}
          max={1}
          step={0.05}
          onChange={(value) => onChange({ ...settings, glowFraction: value })}
        />
        <SliderField
          id="blurFraction"
          label="Blur Fraction"
          value={settings.blurFraction}
          min={0.02}
          max={1}
          step={0.05}
          onChange={(value) => onChange({ ...settings, blurFraction: value })}
        />
        <SliderField
          id="windStrength"
          label="Wind Strength"
          value={settings.windStrength}
          min={0}
          max={2}
          step={0.1}
          onChange={(value) => onChange({ ...settings, windStrength: value })}
        />
        <SliderField
          id="windFraction"
          label="Wind Fraction"
          value={settings.windFraction}
          min={0}
          max={1}
          step={0.05}
          onChange={(value) => onChange({ ...settings, windFraction: value })}
        />
      </SettingsSection>

      <SettingsSection
        title="Atmosphere"
        description="Slow world motion, haze, and depth shaping."
        action={<SectionRandomButton onClick={() => randomizeSection(BLISTER_SECTION_KEYS.atmosphere)} />}
      >
        <SliderField
          id="atmosphereIntensity"
          label="Atmosphere Intensity"
          value={settings.atmosphereIntensity}
          min={0}
          max={1.4}
          step={0.05}
          onChange={(value) => onChange({ ...settings, atmosphereIntensity: value })}
        />
        <SliderField
          id="ambientDriftSpeed"
          label="Ambient Drift Speed"
          value={settings.ambientDriftSpeed}
          min={0}
          max={1.2}
          step={0.05}
          onChange={(value) => onChange({ ...settings, ambientDriftSpeed: value })}
        />
        <SliderField
          id="hazeAmount"
          label="Background Haze"
          value={settings.hazeAmount}
          min={0}
          max={1.2}
          step={0.05}
          onChange={(value) => onChange({ ...settings, hazeAmount: value })}
        />
        <SliderField
          id="fieldDistortion"
          label="Field Distortion"
          value={settings.fieldDistortion}
          min={0}
          max={1.2}
          step={0.05}
          onChange={(value) => onChange({ ...settings, fieldDistortion: value })}
        />
        <SliderField
          id="depthSoftness"
          label="Depth Softness"
          value={settings.depthSoftness}
          min={0}
          max={1.2}
          step={0.05}
          onChange={(value) => onChange({ ...settings, depthSoftness: value })}
        />
      </SettingsSection>

      <SettingsSection
        title="Sparkles"
        description="Micro detail and shimmer accents that keep the scene alive."
        action={<SectionRandomButton onClick={() => randomizeSection(BLISTER_SECTION_KEYS.sparkles)} />}
      >
        <SliderField
          id="sparkleCount"
          label="Sparkle Count"
          value={settings.sparkleCount}
          min={120}
          max={3200}
          step={20}
          onChange={(value) => onChange({ ...settings, sparkleCount: value })}
        />
        <SliderField
          id="sparkleSize"
          label="Sparkle Size"
          value={settings.sparkleSize}
          min={0.08}
          max={0.8}
          step={0.02}
          onChange={(value) => onChange({ ...settings, sparkleSize: value })}
        />
        <SliderField
          id="sparkleOpacity"
          label="Sparkle Opacity"
          value={settings.sparkleOpacity}
          min={0.06}
          max={0.9}
          step={0.02}
          onChange={(value) => onChange({ ...settings, sparkleOpacity: value })}
        />
        <SliderField
          id="sparkleReactivity"
          label="Sparkle Reactivity"
          value={settings.sparkleReactivity}
          min={0}
          max={1.2}
          step={0.05}
          onChange={(value) => onChange({ ...settings, sparkleReactivity: value })}
        />
        <SliderField
          id="sparkleRandomness"
          label="Sparkle Randomness"
          value={settings.sparkleRandomness}
          min={0}
          max={1.2}
          step={0.05}
          onChange={(value) => onChange({ ...settings, sparkleRandomness: value })}
        />
      </SettingsSection>

      <SettingsSection
        title="Reactive Motion"
        description="Gentle breathing, soft trails, and restrained motion echo."
        action={<SectionRandomButton onClick={() => randomizeSection(BLISTER_SECTION_KEYS.reactiveMotion)} />}
      >
        <SliderField
          id="pulseIntensity"
          label="Pulse Intensity"
          value={settings.pulseIntensity}
          min={0}
          max={0.8}
          step={0.05}
          onChange={(value) => onChange({ ...settings, pulseIntensity: value })}
        />
        <SliderField
          id="pulseFrequency"
          label="Pulse Frequency"
          value={settings.pulseFrequency}
          min={0}
          max={0.85}
          step={0.05}
          onChange={(value) => onChange({ ...settings, pulseFrequency: value })}
        />
        <SliderField
          id="trailAmount"
          label="Trail Amount"
          value={settings.trailAmount}
          min={0}
          max={0.5}
          step={0.02}
          onChange={(value) => onChange({ ...settings, trailAmount: value })}
        />
        <SliderField
          id="afterimageAmount"
          label="Afterimage Amount"
          value={settings.afterimageAmount}
          min={0}
          max={0.42}
          step={0.02}
          onChange={(value) => onChange({ ...settings, afterimageAmount: value })}
        />
        <SliderField
          id="flashSoftness"
          label="Flash Softness"
          value={settings.flashSoftness}
          min={0.1}
          max={1}
          step={0.05}
          onChange={(value) => onChange({ ...settings, flashSoftness: value })}
        />
      </SettingsSection>

      <SettingsSection
        title="Post Processing"
        description="Final polish. Keep these restrained for the soft premium look."
        action={<SectionRandomButton onClick={() => randomizeSection(BLISTER_SECTION_KEYS.postProcessing)} />}
      >
        <SliderField
          id="bloomIntensity"
          label="Bloom Intensity"
          value={settings.bloomIntensity}
          min={0}
          max={2.2}
          step={0.05}
          onChange={(value) => onChange({ ...settings, bloomIntensity: value })}
        />
        <SliderField
          id="bloomRadius"
          label="Bloom Softness"
          value={settings.bloomRadius}
          min={0}
          max={1}
          step={0.05}
          onChange={(value) => onChange({ ...settings, bloomRadius: value })}
        />
        <SliderField
          id="grainAmount"
          label="Grain Amount"
          value={settings.grainAmount}
          min={0}
          max={0.22}
          step={0.01}
          onChange={(value) => onChange({ ...settings, grainAmount: value })}
        />
        <SliderField
          id="vignetteAmount"
          label="Vignette Amount"
          value={settings.vignetteAmount}
          min={0}
          max={1}
          step={0.05}
          onChange={(value) => onChange({ ...settings, vignetteAmount: value })}
        />
        <SliderField
          id="chromaticAberration"
          label="Chromatic Softness"
          value={settings.chromaticAberration}
          min={0}
          max={0.35}
          step={0.01}
          onChange={(value) => onChange({ ...settings, chromaticAberration: value })}
        />
      </SettingsSection>
    </div>
  );
}